import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, map } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import {
  selectCurrentUserAllowedNotifications,
  selectCurrentUserPermissions,
} from './permissions.selectors';
import { NotificationId, PermissionId } from './permission.types';

/**
 * Public API for permission checks across the app.
 *
 * The service is intentionally observable-based so templates can use the
 * `async` pipe and changes to the signed-in user's role (e.g. after a
 * server-pushed `USER_ROLE_CHANGED` reloads the profile) propagate
 * automatically without component-level re-subscriptions.
 *
 * For one-shot synchronous checks (effect guards, navigation decisions),
 * see {@link snapshotHas} which reads the current value out of the store.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly store = inject(Store);

  private readonly permissions$ = this.store
    .select(selectCurrentUserPermissions)
    .pipe(distinctUntilChanged());

  private readonly notificationPermissions$ = this.store
    .select(selectCurrentUserAllowedNotifications)
    .pipe(distinctUntilChanged());

  /**
   * Emits `true` whenever the current user holds `permissionId`.
   *
   * Defensive against `null`/`undefined` set values so the service does
   * not throw when consumed by component specs that mock `Store.select`
   * with a fall-through `of(null)`.
   */
  can$(permissionId: PermissionId): Observable<boolean> {
    return this.permissions$.pipe(
      map(set => !!set && set.has(permissionId)),
      distinctUntilChanged()
    );
  }

  /** Emits `true` only when the user holds **every** permission in the list. */
  canAll$(permissionIds: readonly PermissionId[]): Observable<boolean> {
    if (permissionIds.length === 0) {
      return this.permissions$.pipe(map(() => true));
    }
    return combineLatest(permissionIds.map(id => this.can$(id))).pipe(
      map(values => values.every(Boolean)),
      distinctUntilChanged()
    );
  }

  /** Emits `true` when the user holds **at least one** permission in the list. */
  canAny$(permissionIds: readonly PermissionId[]): Observable<boolean> {
    if (permissionIds.length === 0) {
      return this.permissions$.pipe(map(() => false));
    }
    return combineLatest(permissionIds.map(id => this.can$(id))).pipe(
      map(values => values.some(Boolean)),
      distinctUntilChanged()
    );
  }

  /**
   * Emits `true` when the user's role is eligible to receive `notificationId`.
   *
   * Notification permissions are a separate matrix from action permissions —
   * a user may, for example, be able to view a transaction but not receive
   * realtime updates for it, depending on `scope` semantics. See
   * {@link NotificationScope}.
   */
  canReceiveNotification$(notificationId: NotificationId): Observable<boolean> {
    return this.notificationPermissions$.pipe(
      map(set => !!set && set.has(notificationId)),
      distinctUntilChanged()
    );
  }

  /**
   * Synchronous snapshot. Reads the current store value once.
   *
   * Prefer the observable variants in templates and effects. Use this
   * inside event handlers or guards where wiring up a subscription would
   * be more friction than benefit.
   */
  snapshotHas(permissionId: PermissionId): boolean {
    let result = false;
    this.store
      .select(selectCurrentUserPermissions)
      .subscribe(set => (result = !!set && set.has(permissionId)))
      .unsubscribe();
    return result;
  }

  /**
   * Synchronous notification snapshot. Used by the notification dispatcher
   * to drop events the user is not authorised to see before they reach the
   * bell. See `shared/utils/build-notification.ts`.
   */
  snapshotCanReceiveNotification(notificationId: NotificationId): boolean {
    let result = false;
    this.store
      .select(selectCurrentUserAllowedNotifications)
      .subscribe(set => (result = !!set && set.has(notificationId)))
      .unsubscribe();
    return result;
  }
}
